/**
 * D74 — ShareWithControl
 *
 * Reusable "Share with" multi-select for notes (My Notes, Meeting Notes,
 * Client Pages). Renders an inline button + popover; popover contains a
 * search input + filterable list of workspace members with checkboxes,
 * plus a selected-members badge strip. Empty list = "private to me" for
 * My Notes, or "team-collaborative" for Meeting Notes (caller decides
 * the empty-state label via `emptyLabel`).
 *
 * Props:
 *   workspaceId  — current workspace id
 *   noteId       — the resource being shared (used for data-testids)
 *   value        — current array of user_ids
 *   onChange     — async (newIds: string[]) => void
 *   excludeUserId — owner's own id (silently filtered from list)
 *   emptyLabel   — text for the empty selection state
 *   disabled     — bool (e.g. while saving)
 */
import { useEffect, useMemo, useState } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Users, X, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ShareWithControl({
  workspaceId,
  noteId,
  value = [],
  onChange,
  excludeUserId,
  emptyLabel = 'Private — only you',
  disabled = false,
}) {
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);

  // Load workspace members once per workspace open.
  useEffect(() => {
    if (!workspaceId || !open || members.length > 0) return;
    setLoadingMembers(true);
    fetch(`${API}/api/workspaces/${workspaceId}/members-lite`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('stellar_token')}` },
    })
      .then((r) => r.json())
      .then((rows) => {
        if (Array.isArray(rows)) setMembers(rows);
      })
      .catch(() => toast.error('Failed to load workspace members'))
      .finally(() => setLoadingMembers(false));
  }, [workspaceId, open, members.length]);

  const selectedSet = useMemo(() => new Set(value || []), [value]);
  const selectedRows = useMemo(
    () => members.filter((m) => selectedSet.has(m.id)),
    [members, selectedSet],
  );

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return members
      .filter((m) => m.id !== excludeUserId)
      .filter((m) =>
        !q ||
        (m.name || '').toLowerCase().includes(q) ||
        (m.email || '').toLowerCase().includes(q));
  }, [members, excludeUserId, filter]);

  const toggle = async (uid) => {
    if (busy || disabled) return;
    const next = new Set(value || []);
    if (next.has(uid)) next.delete(uid);
    else next.add(uid);
    const arr = Array.from(next);
    setBusy(true);
    try {
      await onChange?.(arr);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update sharing');
    } finally {
      setBusy(false);
    }
  };

  const removeOne = async (uid) => toggle(uid);

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`share-with-${noteId || 'control'}`}>
      <Popover open={open} onOpenChange={setOpen} modal={false}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            className="h-7 text-[12px] gap-1.5"
            data-testid={`share-with-trigger-${noteId || 'control'}`}
          >
            <Users className="h-3 w-3" />
            Share with
            {value.length > 0 && (
              <span className="ml-0.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-gold/20 text-gold text-[10px] font-semibold">
                {value.length}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-[320px] p-0 pointer-events-auto"
          data-testid={`share-with-popover-${noteId || 'control'}`}
        >
          <div className="p-2 border-b border-border/40">
            <Input
              autoFocus
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search members…"
              className="h-8 text-[12px]"
              data-testid={`share-with-search-${noteId || 'control'}`}
            />
          </div>
          <div className="max-h-[260px] overflow-y-auto p-1">
            {loadingMembers ? (
              <div className="py-6 text-center text-[12px] text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin inline-block mr-1.5" />
                Loading members…
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-6 text-center text-[12px] text-muted-foreground">
                No matching members
              </div>
            ) : (
              filtered.map((m) => {
                const checked = selectedSet.has(m.id);
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => toggle(m.id)}
                    disabled={busy}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-[12px] hover:bg-foreground/[0.04] ${checked ? 'bg-foreground/[0.03]' : ''}`}
                    data-testid={`share-with-member-${m.id}`}
                  >
                    <span className={`h-4 w-4 rounded border ${checked ? 'bg-gold border-gold' : 'border-border'} flex items-center justify-center shrink-0`}>
                      {checked && <Check className="h-3 w-3 text-black" />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{m.name || m.email || m.id}</div>
                      {m.email && m.email !== m.name && (
                        <div className="text-[10px] text-muted-foreground/60 truncate">{m.email}</div>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </PopoverContent>
      </Popover>

      {selectedRows.length === 0 ? (
        <span className="text-[11px] text-muted-foreground/60" data-testid="share-with-empty-label">
          {emptyLabel}
        </span>
      ) : (
        <div className="flex flex-wrap items-center gap-1">
          {selectedRows.map((m) => (
            <Badge
              key={m.id}
              variant="secondary"
              className="text-[10px] gap-1 pr-1"
              data-testid={`share-with-pill-${m.id}`}
            >
              {m.name || m.email}
              <button
                type="button"
                onClick={() => removeOne(m.id)}
                disabled={busy || disabled}
                className="hover:text-destructive"
                aria-label={`Unshare with ${m.name || m.email}`}
                data-testid={`share-with-remove-${m.id}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          {busy && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
        </div>
      )}
    </div>
  );
}
